/**
 * The requisition-slip wire types.
 *
 * **THE ENUMS ARE NUMERIC, because that is what the API serializes.** No
 * `JsonStringEnumConverter` is configured, so a body carrying `"stage": "Draft"` is refused
 * outright, and a string union here would typecheck perfectly while making every comparison
 * silently false. The convention is `PR_STATUS`'s: an `as const` object of numbers, with the
 * LABEL coming from the server on the DTO rather than from a second map here.
 *
 * The FACET values are the exception and are strings — the server emits enum NAMES there and
 * parses them with `Enum.TryParse` — so a facet selection passes straight through untouched.
 */

/**
 * Where a slip is in its own lifecycle.
 *
 * **FIVE of the server's ten, and the five it declares are the five anything can write.**
 * `ForSpsApproval 3`, `ReadyForRelease 4`, `PartiallyIssued 5`, `Issued 6` and `Closed 9` are
 * reserved server-side for fulfilment, which is not built — naming them here would be states
 * nothing can produce, and the union is the one thing `tsc` cannot check against the wire.
 *
 * They are added here in the SAME change that makes one writable, which is the half the
 * purchase request's own file missed: `PR_STATUS` went a week without the two statuses its
 * server had started writing, and nothing was visibly broken because the label is the
 * server's — every comparison was simply false.
 */
export const RIS_STAGE = {
  draft: 0,
  submitted: 1,
  approved: 2,
  rejected: 7,
  cancelled: 8,
} as const;

export type RequisitionSlipStage = (typeof RIS_STAGE)[keyof typeof RIS_STAGE];

/** One requested line, as read back. */
export interface RequisitionSlipLine {
  id: string;
  inventoryItemId: string;
  itemName: string | null;
  sku: string | null;
  manufacturer: string | null;
  brandName: string | null;
  model: string | null;
  unitOfMeasure: string | null;
  requestedQuantity: number;
  /** What was on hand when the slip was submitted. Advisory, and zero until then. */
  availableAtRequestQuantity: number;
  remarks: string | null;
  isActive: boolean;
}

/** One slip as the register lists it. */
export interface RequisitionSlipListItem {
  id: string;
  slipNumber: string;
  risNumber: string | null;
  stage: RequisitionSlipStage;
  /** The server's word for the stage — never derived from a second map here. */
  stageLabel: string;
  requestingOrgUnitId: string | null;
  requestingOrgUnitName: string | null;
  storageFacilityId: string | null;
  storageFacilityName: string | null;
  /** An id, never a name — the register cannot show or sort by who it is. */
  accountablePersonnelId: string | null;
  purpose: string | null;
  lineCount: number;
  totalRequestedQuantity: number;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** One slip in full. */
export interface RequisitionSlipDetail {
  id: string;
  slipNumber: string;
  risNumber: string | null;
  stage: RequisitionSlipStage;
  stageLabel: string;
  requestingOrgUnitId: string | null;
  requestingOrgUnitName: string | null;
  storageFacilityId: string | null;
  storageFacilityName: string | null;
  accountablePersonnelId: string | null;
  purpose: string | null;
  notes: string | null;
  cancellationReason: string | null;
  /** Derived server-side, so the form's disabled reason and the endpoint cannot disagree. */
  isEditable: boolean;
  canSubmit: boolean;
  canCancel: boolean;
  items: RequisitionSlipLine[];
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

/**
 * One item a line may name.
 *
 * **It includes items at ZERO stock**, which is the whole difference from the issuance
 * module's `issuable-items`: requesting what is not there is how Logistics learns to restock
 * it, and `availableQuantity` is advisory rather than a cap.
 */
export interface RequestableItem {
  id: string;
  sku: string;
  itemName: string | null;
  manufacturer: string | null;
  brandName: string | null;
  model: string | null;
  unitOfMeasure: string | null;
  availableQuantity: number;
}

/**
 * The signed-in account's own employee identity.
 *
 * **It fails SOFT** — `error` is set and the rest is null, rather than the request failing.
 */
export interface CurrentRequester {
  personnelId: string | null;
  orgUnitId: string | null;
  orgUnitName: string | null;
  error: string | null;
}

/** One line as the form supplies it. No id: the set is addressed as a whole. */
export interface RequisitionSlipLineInput {
  inventoryItemId: string;
  requestedQuantity: number;
  remarks: string | null;
}

/** Raise or correct a slip. */
export interface RequisitionSlipRequest {
  storageFacilityId: string | null;
  accountablePersonnelId: string | null;
  purpose: string | null;
  notes: string | null;
  items: RequisitionSlipLineInput[];
  submitImmediately?: boolean;
}
